import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, message, CountDownInput } from 'antd';
import { MailOutlined, LockOutlined, CheckCircleOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { userAPI } from '../services/api';
import { getApiUrl } from '../utils/apiUrl';

const Register = () => {
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(true); // 默认启用
  const [agreementChecked, setAgreementChecked] = useState(false);
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const [searchParams] = useSearchParams();
  const referrerCode = searchParams.get('ref') || localStorage.getItem('referrer_code') || '';
  const campaignCode = searchParams.get('campaign') || localStorage.getItem('campaign_code') || '';
  const redirectUrl = searchParams.get('redirect') || '/dashboard';
  const [bgImage, setBgImage] = useState('');
  const [bgLoaded, setBgLoaded] = useState(false);
  const [legalLinks, setLegalLinks] = useState({
    terms: '/terms',
    privacy: '/privacy'
  });

  // 处理图片URL，相对路径转为绝对路径
  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // 避免双斜杠问题
    const base = getApiUrl('');
    if (base.endsWith('/') && url.startsWith('/')) {
      return base + url.substring(1);
    }
    return `${base}${url}`;
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem('lang') || 'en');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 清理存储的活动码（注册完成后或页面关闭时）
  useEffect(() => {
    return () => {
      // 页面卸载时不清除，让注册完成后清除
    };
  }, []);

  // 获取配置
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // 直接调用后端API
        const response = await fetch('/api/configs');
        const data = await response.json();
        if (data.code === 0) {
          // 获取邮件配置
          const emailConf = data.data.list?.find(c => c.config_key === 'email_enabled');
          if (emailConf) {
            setEmailEnabled(emailConf.config_value === 'true');
          }
          // 获取注册页背景图
          const bgConf = data.data.list?.find(c => c.config_key === 'register_background');
          if (bgConf && bgConf.config_value) {
            // 预加载图片避免闪烁
            const img = new Image();
            img.onload = () => {
              setBgImage(bgConf.config_value);
              // 延迟一点再显示，确保平滑过渡
              setTimeout(() => setBgLoaded(true), 50);
            };
            img.src = getFullImageUrl(bgConf.config_value);
          }
        }
      } catch (error) {
        console.log('获取配置失败:', error);
        // 配置获取失败时，默认关闭邮件验证（更友好的体验）
        setEmailEnabled(false);
      }
    };
    fetchConfig();

    // 获取首页配置（用于隐私政策和服务条款链接）
    const fetchHomepageConfig = async () => {
      try {
        const response = await fetch('/api/homepage-config');
        const data = await response.json();
        if (data.code === 0 && data.data) {
          // 检查是否有公司链接配置
          let companyLinks = [];
          try {
            companyLinks = JSON.parse(data.data.footer_company_links || '[]');
          } catch (e) {}

          // 查找服务条款和隐私政策链接
          const termsLink = companyLinks.find(l => l.title?.includes('服务') || l.title?.includes('Terms'));
          const privacyLink = companyLinks.find(l => l.title?.includes('隐私') || l.title?.includes('Privacy'));

          setLegalLinks({
            terms: termsLink?.url || '/terms',
            privacy: privacyLink?.url || '/privacy'
          });
        }
      } catch (error) {
        console.log('获取首页配置失败:', error);
      }
    };
    fetchHomepageConfig();
  }, []);

  const t = (zh, en) => (lang === 'zh' ? zh : en);

  const sendVerifyCode = async () => {
    const email = document.querySelector('input[name="email"]')?.value;
    if (!email) {
      message.error(t('请先输入邮箱', 'Please enter email first'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      message.error(t('请输入有效的邮箱地址', 'Please enter a valid email'));
      return;
    }
    
    setSendingCode(true);
    try {
      const response = await fetch('/api/auth/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, type: 'register' }),
      });
      const data = await response.json();
      if (data.code === 0) {
        message.success(t('验证码已发送到邮箱', 'Verification code sent to email'));
        setCodeSent(true);
      } else {
        // 检查是否是邮件服务未启用
        if (data.message && data.message.includes('邮件服务未启用')) {
          setEmailEnabled(false);
          message.warning(t('邮件服务已关闭，无需验证码', 'Email service is disabled, no verification needed'));
        } else {
          message.error(data.message || t('发送失败', 'Send failed'));
        }
      }
    } catch (error) {
      message.error(t('发送失败，请稍后重试', 'Send failed, please try again'));
    } finally {
      setSendingCode(false);
    }
  };

  const onFinish = async (values) => {
    if (values.password !== values.confirmPassword) {
      message.error(t('两次输入的密码不一致', 'Passwords do not match'));
      return;
    }

    // 如果邮件服务启用但没有验证码，返回
    if (emailEnabled && !values.verifyCode) {
      message.error(t('请输入验证码', 'Please enter verification code'));
      return;
    }

    // 检查协议勾选
    if (!agreementChecked) {
      message.error(t('请阅读并同意服务条款和隐私政策', 'Please read and agree to the Terms of Service and Privacy Policy'));
      return;
    }

    setLoading(true);
    try {
      // 邮件服务关闭时不传验证码
      const verifyCode = emailEnabled ? values.verifyCode : '';
      // 注册时传递推荐码和活动码
      const response = await userAPI.register(values.email, values.password, referrerCode, verifyCode, campaignCode);
      if (response.code === 0) {
        // 清除存储的活动码
        localStorage.removeItem('campaign_code');
        localStorage.removeItem('referrer_code');
        
        message.success(t('注册成功，正在自动登录...', 'Registration successful, logging in...'));
        try {
          const loginRes = await userAPI.login(values.email, values.password);
          if (loginRes.code === 0 && loginRes.data) {
            localStorage.setItem('user_token', loginRes.data.token);
            setTimeout(() => {
              window.location.href = redirectUrl;
            }, 500);
          } else {
            message.error(t('自动登录失败，请手动登录', 'Auto login failed, please login manually'));
            setTimeout(() => {
              navigate('/login?redirect=' + encodeURIComponent(redirectUrl));
            }, 1500);
          }
        } catch (e) {
          message.error(t('自动登录失败，请手动登录', 'Auto login failed, please login manually'));
          setTimeout(() => {
            navigate('/login?redirect=' + encodeURIComponent(redirectUrl));
          }, 1500);
        }
      } else {
        message.error(response.message || t('注册失败', 'Registration failed'));
      }
    } catch (error) {
      message.error(error.response?.data?.message || t('注册失败，请稍后重试', 'Registration failed, please try again'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {bgImage && (
        <div 
          className={`auth-bg-layer ${bgLoaded ? 'loaded' : ''}`}
          style={{
            backgroundImage: `url(${getFullImageUrl(bgImage)})`,
          }}
        />
      )}
      <div className="auth-card">
        <div className="auth-logo">
          <img src={require('../utils/img/logo.png')} alt="Blue API" className="auth-logo-icon" />
          <h1>{t('创建账号', 'Create Account')}</h1>
          <p>{t('注册即可获得免费体验额度', 'Register to get free credits')}</p>
        </div>

        <Form name="register" onFinish={onFinish} autoComplete="off" size="large">
          <Form.Item
            name="email"
            rules={[
              { required: true, message: t('请输入邮箱', 'Please enter email') },
              { type: 'email', message: t('请输入有效的邮箱地址', 'Please enter a valid email') },
            ]}
          >
            <Input
              name="email"
              prefix={<MailOutlined style={{ color: '#9ca3af', fontSize: 16 }} />}
              placeholder={t('邮箱地址', 'Email address')}
              style={{ height: 44, borderRadius: 8 }}
            />
          </Form.Item>

          {emailEnabled && (
            <Form.Item
              name="verifyCode"
              rules={[
                { required: true, message: t('请输入验证码', 'Please enter verification code') },
                { len: 6, message: t('验证码为6位', 'Code must be 6 digits') },
              ]}
            >
              <div style={{ display: 'flex', gap: 8 }}>
                <Input
                  prefix={<SafetyOutlined style={{ color: '#9ca3af', fontSize: 16 }} />}
                  placeholder={t('邮箱验证码', 'Email verification code')}
                  maxLength={6}
                  style={{ flex: 1, height: 44, borderRadius: 8 }}
                />
                <Button
                  onClick={sendVerifyCode}
                  loading={sendingCode}
                  style={{ 
                    height: 44, 
                    borderRadius: 8,
                    minWidth: 110,
                    fontSize: 13,
                  }}
                >
                  {codeSent ? t('重新发送', 'Resend') : t('获取验证码', 'Get Code')}
                </Button>
              </div>
            </Form.Item>
          )}

          <Form.Item
            name="password"
            rules={[
              { required: true, message: t('请输入密码', 'Please enter password') },
              { min: 8, message: t('密码至少8位', 'Password must be at least 8 characters') },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#9ca3af', fontSize: 16 }} />}
              placeholder={t('设置密码（至少8位）', 'Set password (at least 8 characters)')}
              style={{ height: 44, borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: t('请确认密码', 'Please confirm password') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('两次输入的密码不一致', 'Passwords do not match')));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<CheckCircleOutlined style={{ color: '#9ca3af', fontSize: 16 }} />}
              placeholder={t('确认密码', 'Confirm password')}
              style={{ height: 44, borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={agreementChecked}
                onChange={(e) => setAgreementChecked(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 13, color: '#6b7280', lineHeight: '20px' }}>
                {t('我已阅读并同意', 'I have read and agree to the')}
                <Link to={legalLinks.terms} style={{ color: '#1677ff' }}> {t('服务条款', 'Terms of Service')} </Link>
                {t('和', 'and')}
                <Link to={legalLinks.privacy} style={{ color: '#1677ff' }}> {t('隐私政策', 'Privacy Policy')}</Link>
              </span>
            </label>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{
                height: 44,
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 500,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
              }}
            >
              {t('注 册', 'Register')}
            </Button>
          </Form.Item>
        </Form>

        <div className="auth-footer" style={{ textAlign: 'center' }}>
          {t('已有账号？', 'Already have an account?')} <Link to="/login">{t('立即登录', 'Login now')}</Link>
          <span style={{ margin: '0 12px', color: '#d9d9d9' }}>|</span>
          <Link to="/">{t('返回官网', 'Back to Home')}</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
